import { awardCriteriaPlan, currentValueAt, listChange, listItemLabel } from "./confirmationCardFormatting";

type Row = Record<string, unknown>;

export type CardRemovals = { removed: string[]; renamed: { from: string; to: string }[] };

const NOT_CHANGES = new Set(["fieldSources", "expectedCurrent"]);

export const removalsOnCard = (patch: Row, current: Row): CardRemovals => {
  const found: CardRemovals = { removed: [], renamed: [] };
  walk(patch, [], current, found);
  return found;
};

export const removesOrRenames = (removals: CardRemovals): boolean =>
  removals.removed.length > 0 || removals.renamed.length > 0;

const walk = (node: unknown, path: string[], current: Row, found: CardRemovals): void => {
  if (path[path.length - 1] === "awardCriteria" && isRow(node)) {
    criteriaRemovals(node, currentValueAt(current, path), found);
    return;
  }
  if (Array.isArray(node)) {
    listRemovals(node, currentValueAt(current, path), found);
    return;
  }
  if (!isRow(node)) return;
  Object.entries(node)
    .filter(([key]) => !(path.length === 0 && NOT_CHANGES.has(key)))
    .forEach(([key, value]) => walk(value, [...path, key], current, found));
};

const criteriaRemovals = (proposed: Row, current: unknown, found: CardRemovals): void => {
  const plan = awardCriteriaPlan(proposed, current, {});
  if (plan.kind !== "bestPriceQualityRatio") return;
  plan.rows.forEach((row) => {
    if (row.change === "removed") found.removed.push(row.label);
    renameIn(row.details, found);
    row.subCriteria.forEach((sub) => {
      if (sub.change === "removed") found.removed.push(`${row.label}: ${sub.label}`);
      renameIn(sub.details, found);
    });
  });
};

const listRemovals = (proposed: unknown[], current: unknown, found: CardRemovals): void => {
  if (!Array.isArray(current)) return;
  const change = listChange(proposed, current);
  change.removed.forEach((item) => found.removed.push(listItemLabel(item, { resolveEntityName: () => undefined })));
  change.changed.forEach((item) => renameIn(item.fields, found));
};

const renameIn = (fields: { path: string[]; value: unknown; previous?: unknown }[], found: CardRemovals): void => {
  const name = fields.find((field) => field.path.length === 1 && field.path[0] === "name");
  if (name && typeof name.previous === "string" && name.previous.trim() && typeof name.value === "string") {
    found.renamed.push({ from: name.previous, to: name.value });
  }
};

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
